import axios from 'axios';
import { useAuthStore } from '../store/authStore';

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

// Double-submit CSRF only matters for cookie-only-authenticated,
// state-changing requests on /api/marketplace, /api/admin, /api/magazine,
// /api/print-ads, /api/support, /api/coupons (see server/src/app.js) —
// /api/auth (login/signup/reset) is explicitly exempt server-side, so we
// never even try to fetch a token for it. And if the token fetch itself
// ever fails for a route that DOES need it, we let the real request
// through anyway rather than blocking it on our own preflight — the
// server's own 403 (if the route actually needed the token) is a more
// useful signal than a fetch failure a caller never asked for.
const CSRF_EXEMPT_PREFIXES = ['/auth/'];
const SAFE_METHODS = new Set(['get', 'head', 'options']);

let csrfTokenPromise = null;
async function getCsrfToken(forceRefresh = false) {
  if (forceRefresh || !csrfTokenPromise) {
    csrfTokenPromise = axios
      .get('/api/csrf-token', { withCredentials: true })
      .then((res) => res.data.csrfToken);
  }
  return csrfTokenPromise;
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || 'get').toLowerCase();
  const isExempt = CSRF_EXEMPT_PREFIXES.some((p) => (config.url || '').startsWith(p));
  if (!SAFE_METHODS.has(method) && !isExempt) {
    try {
      config.headers['x-csrf-token'] = await getCsrfToken();
    } catch (err) {
      console.error('Failed to fetch CSRF token; proceeding without it:', err);
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logout();
    }
    if (
      err.response?.status === 403 &&
      err.response?.data?.error?.toLowerCase().includes('csrf') &&
      !err.config.__csrfRetried
    ) {
      err.config.__csrfRetried = true;
      try {
        await getCsrfToken(true);
        err.config.headers['x-csrf-token'] = await csrfTokenPromise;
        return api.request(err.config);
      } catch {
        // fall through to the original rejection below
      }
    }
    return Promise.reject(err);
  }
);

export default api;
